import { useCallback, useEffect, useState } from "react";
import PlayerHeader from "../components/PlayerHeader";
import NewVideoPlayer from '../components/NewVideoPlayer';
import '../App.css';
import { mockRssXml } from '../mockVpvData';
import { TextExpansionProvider } from '../contexts/TextExpansionContext';
import { useBrand } from '../contexts/BrandContext';

// Browser-compatible XML to JSON conversion function
const xmlToJson = (xml) => {
  const parser = new DOMParser();
  
  // Pre-process XML to fix common malformed attribute issues
  const cleanedXml = xml
    .replace(/url=""/g, 'url="') // Fix double quotes at start of URL
    .replace(/""([^"]*)""/g, '"$1"') // Fix double quotes around values
    .replace(/length=""/g, 'length="0"') // Fix empty length attributes
    .replace(/type=""/g, 'type="audio/mp3"') // Fix empty type attributes
    .replace(/&(?!(amp|lt|gt|quot|apos|#[0-9]+|#x[0-9a-fA-F]+);)/g, '&amp;');
  
  const xmlDoc = parser.parseFromString(cleanedXml, "text/xml");
  
  // Check for parsing errors
  const parseError = xmlDoc.getElementsByTagName('parsererror')[0];
  if (parseError) {
    console.error('XML Parse Error:', parseError.textContent);
    throw new Error('XML parsing failed: ' + parseError.textContent);
  }
  
  const parseNode = (node) => {
    const obj = {};
    
    // Handle attributes
    if (node.attributes && node.attributes.length > 0) {
      obj._attributes = {};
      for (let i = 0; i < node.attributes.length; i++) {
        const attr = node.attributes[i];
        obj._attributes[attr.name] = attr.value;
      }
    }
    
    // Handle child nodes
    if (node.hasChildNodes()) {
      for (let i = 0; i < node.childNodes.length; i++) {
        const child = node.childNodes[i];
        
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent.trim();
          if (text) {
            obj._text = text;
          }
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const nodeName = child.nodeName;
          const childObj = parseNode(child);
          
          if (obj[nodeName]) {
            if (!Array.isArray(obj[nodeName])) {
              obj[nodeName] = [obj[nodeName]];
            }
            obj[nodeName].push(childObj);
          } else {
            obj[nodeName] = childObj;
          }
        }
      }
    }
    
    return obj;
  };
  
  return parseNode(xmlDoc.documentElement);
};

const FALLBACK_IMAGE = window.broadcastPlayerData?.fallbackImageUrl || 'https://insightk.kinsta.cloud/wp-content/uploads/2025/04/broadcast-600x400.jpg';
const DEFAULT_PREROLL_AD_URL = window.broadcastPlayerData?.prerollAdUrl || 'https://insight.org/media/xml/vast-advent.php?media=2';
const DEFAULT_POSTROLL_AD_URL = window.broadcastPlayerData?.postrollAdUrl || 'https://insight.org/media/xml/vast-chuck-jeffress.php';

// Utility function to parse duration (seconds, HH:MM:SS, etc)
const parseDuration = (duration) => {
    if (!duration) return 0;
    
    // If it's already a number, return it
    if (typeof duration === 'number') return duration;
    
    // If it's a string that is just a number (e.g. "2219"), parse it
    if (/^\d+$/.test(duration)) {
        return parseInt(duration, 10);
    }
    
    // Handle HH:MM:SS or MM:SS format
    const parts = duration.split(':').map(Number);
    if (parts.length === 3) {
        const [hours, minutes, seconds] = parts;
        return (hours * 3600) + (minutes * 60) + seconds;
    }
    if (parts.length === 2) {
        const [minutes, seconds] = parts;
        return (minutes * 60) + seconds;
    }
    
    return 0;
};

function BroadcastPlayerContent() {
    const { 
        translations: t, 
        isVPVBranded, 
        getThemeClass, 
        customColor, 
        textColor,
        setVpvTheme,
        setCustomColor,
        setTextColor,
        vpvTheme
    } = useBrand();
    
    const [content, setContent] = useState(null);
    const [series, setSeries] = useState(null);
    const [error, setError] = useState(null);
    const [playlistItems, setPlaylistItems] = useState(null);
    const [initialIndex, setInitialIndex] = useState(null);
    const [currentPlaylistItem, setCurrentPlaylistItem] = useState({
        name: "Sample Message Title",
        broadcastDescription: "This is a sample description for the broadcast.",
        seriesName: "Sample Series Name",
        seriesDateRange: "2023-2024",
    });

    const loadPlaylist = (jsonData, jsonData3) => {
        console.log('Calling loadPlaylist...' +  {jsonData})
        let tempArray = [];
        for (const element of jsonData) {
            console.log(element);
            const seriesImg = jsonData3?.guid?.rendered;
            const secureUrl = seriesImg?.replace('http://','https://');
            const itemName = element?.acf_fields?.broadcast_title ?
            element?.acf_fields?.broadcast_title + ", " + t.part + " " + element?.acf_fields?.broadcast_part :
            element?.title + ", " + t.message + " " + element?.acf_fields?.message_number;

            const {url, length, type} = element?.enclosure?._attributes;
            console.log('Enclosure attributes:', {url, length, type});

            const itunesImage = element['itunes:image']?._attributes?.href;
            const thumbnailImg = element['ifl:broadcastImage']?._text || element['ifl:seriesImage']?._text || itunesImage || FALLBACK_IMAGE;
            
            // Decode HTML entities (e.g. &#8217; → ')
            const decodeEntities = (str) => {
                if (!str) return '';
                const txt = document.createElement('textarea');
                txt.innerHTML = str;
                return txt.value;
            };

            // An empty <ifl:adSyncUrl> in the feed means ads are intentionally
            // disabled for this episode (e.g. air date outside the ad campaign
            // window) — don't fall back to the default ad URLs. Only fall back
            // when the element is missing entirely.
            const adSyncElement = element['ifl:adSyncUrl'];
            const adSyncUrl = adSyncElement ? (adSyncElement._text || '') : undefined;

            let item = {
                sources: [{ src: url, type: type }],
                poster:  element['ifl:seriesImage']?._text || itunesImage || FALLBACK_IMAGE,
                name: decodeEntities(element?.title?._text),
                description: element['ifl:broadcastAirDate']?._text || '',
                notes: element['ifl:notesURL']?._text || '',
                message: element['ifl:messageURL']?._text || '',
                series: element['ifl:seriesURL']?._text || '',
                seriesDateRange: element['ifl:seriesDateRange']?._text || '',
                seriesName: element['itunes:subtitle']?._text || '',
                thumbnail: [{ src: thumbnailImg, class: 'vjs-playlist-thumbnail' }],
                broadcastDescription: element['itunes:summary']?._text || '',
                seriesPage: element['ifl:seriesPage']?._text || '',
                guid: element?.guid?._text || '',
                prerollAdUrl: adSyncUrl !== undefined ? adSyncUrl : DEFAULT_PREROLL_AD_URL,
                duration: parseDuration(element['itunes:duration']?._text),
                postrollAdUrl: adSyncUrl !== undefined ? adSyncUrl : DEFAULT_POSTROLL_AD_URL,
                mediaType: type?.startsWith('video/') ? 'video' : 'audio',
            }
            tempArray.push(item);
        }
        
        const queryParams = new URLSearchParams(window.location.search);
        const bid = queryParams.get('bid');
        if (bid) {
            const index = tempArray.findIndex(item => item.guid === bid);
            if (index !== -1) {
                setInitialIndex(index);
                setCurrentPlaylistItem(tempArray[index]);
            }
        }
        
        setPlaylistItems(tempArray);
    }

    const handlePlaylistItemChange = (item) => {
        setCurrentPlaylistItem(item);
    };

    const loadContent = useCallback(async () => {
        let xmlString;
        try {
            const targetUrl = window.broadcastPlayerData?.rssFeedUrl || `${window.location.origin}/feed/RSSBroadcast`;
            const isReactDevMode = window.location.port === '3000';
            
            if (isReactDevMode) {
                xmlString = mockRssXml;
            } else {
                const restUrl = window.broadcastPlayerData?.restUrl || '/wp-json/wp/v2/';
                const proxyUrl = `${restUrl}proxy-rss`;
                const nonce = window.broadcastPlayerData?.nonce || '';
                
                const headers = { 'Content-Type': 'application/json' };
                if (nonce) headers['X-WP-Nonce'] = nonce;
                
                const response = await fetch(proxyUrl, {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify({ url: targetUrl })
                });
                
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const result = await response.json();
                if (result.status !== 'success') throw new Error('Failed to fetch RSS feed from proxy');
                xmlString = result.content;
            }
            
            let dataObject = xmlToJson(xmlString);
            const currentContent = dataObject?.channel?.item;

            let contentArray = [];
            if (Array.isArray(currentContent)) {
                contentArray = currentContent;
            } else if (currentContent) {
                contentArray = [currentContent];
            }
            
            contentArray?.reverse();
            loadPlaylist(contentArray);
            
        } catch (error) {
            setError(new Error('Failed to load RSS content: ' + error.message));
        }
    }, []);

    useEffect(()=> {
        document.title = 'Broadcast Player';
        loadContent();
    }, [loadContent]);

    return (
        <div 
            id="media-container" 
            className={`media-container broadcast-player ${getThemeClass()} ${isVPVBranded ? 'vpv-branded' : ''}`} 
            style={{
                display: "flex", 
                flexDirection: "column",
                '--vpv-theme-color': customColor,
                '--vpv-text-color': textColor
            }}
        >
            {error && <div className="error">Error: {error.message}</div>}
            {currentPlaylistItem?.name && <PlayerHeader 
                content={content} 
                seriesData={series} 
                currentPlaylistItem={currentPlaylistItem}
                vpvTheme={vpvTheme}
                customColor={customColor}
                textColor={textColor}
                onThemeChange={(theme, color) => {
                    setVpvTheme(theme);
                    if (color) setCustomColor(color);
                }}
                onTextColorChange={setTextColor}
                isVPVBranded={isVPVBranded}
            />}
            <div id='player-container' style={{
                transition: 'all 0.3s ease-in-out'
            }}>
                {playlistItems && playlistItems.length > 0 && (
                    <NewVideoPlayer 
                        playlist={playlistItems} 
                        onPlaylistItemChange={handlePlaylistItemChange}
                        currentPlaylistItem={currentPlaylistItem}
                        isSeries={false}
                        prerollAdEnabled={isVPVBranded}
                        postrollAdEnabled={false}
                        initialIndex={initialIndex}
                        textColor={textColor}
                    />
                )}
                {(!playlistItems || playlistItems.length === 0) && !error && (
                    <div style={{padding: '20px', textAlign: 'center', color: '#fff'}}>
                        {t.loadingPlayer}
                    </div>
                )}
            </div>
        </div>
    );
}

function BroadcastPlayer() {
    return (
        <TextExpansionProvider>
            <BroadcastPlayerContent />
        </TextExpansionProvider>
    );
}

export default BroadcastPlayer;
